/**
 * The shape of every row this dashboard's five views return, plus the handful of constants the
 * panels share. One file so the panels cannot drift apart from each other or from
 * `winsight-server`'s `reports/t2-business-dashboard/report.yaml`, which is authoritative.
 *
 * Every ratio here arrives already computed: SDP is `SUM(on time) / SUM(delivered)` in ADB, and a
 * share is a share of the whole filtered result, not of the rows on screen (§4.3, [DATA-1]). The
 * `*_on_time_qty` / `*_delivered_qty` pairs travel with each percentage so a panel can say what a
 * figure is made of without ever re-deriving it.
 */

import { LOCALE } from '@/lib/locale';

/**
 * The one global SDP target, as a percentage.
 *
 * Owner-set, taken from the "KPI Target" column of the users' own sheet (`materials
 * reporting.xlsx`, sheet "T2 performance report", cells C6/C10 = 1). It is one number for every
 * supplier, so it is a reference line here rather than a column repeated down every result — the
 * treatment the T2 Business SDP view settled on.
 *
 * Deliberately no intermediate amber band. The design mock derived one from `target - 8` and
 * `target - 20`; those widths have no owner behind them and PORT-2 forbids inventing a business
 * threshold. A figure is at target or below it, and nothing on this page grades the gap.
 */
export const SDP_TARGET_PCT = 100;

/** Ring discriminator on the `by-supplier` view: the inner ring is material type, the outer supplier. */
export type Ring = 'class' | 'supplier';

export interface KpiRow {
  ordered_measure: number | null;
  received_measure: number | null;
  open_measure: number | null;
  past_due_measure: number | null;
  received_share: number | null;
  open_share: number | null;
  past_due_share: number | null;
  sdp_pct: number | null;
  sdp_on_time_qty: number | null;
  sdp_delivered_qty: number | null;
  po_count: number | null;
  open_po_count: number | null;
  supplier_count: number | null;
}

export interface SupplierRingRow {
  ring: Ring;
  material_class: string | null;
  /** Null on a class row; an "Other <class>" label on the folded remainder. */
  t2_group: string | null;
  rank_no: number | null;
  /** How many suppliers a row stands for — more than one only on a folded "Other" row. */
  supplier_count: number | null;
  measure_value: number | null;
  share_pct: number | null;
  sdp_pct: number | null;
  sdp_on_time_qty: number | null;
  sdp_delivered_qty: number | null;
}

export interface FactoryRow {
  /** The site the source's several codes are grouped into — see `V6__t2_vendor_spend_factory_group.sql`. */
  factory_group: string | null;
  measure_value: number | null;
  share_pct: number | null;
  sdp_pct: number | null;
  sdp_on_time_qty: number | null;
  sdp_delivered_qty: number | null;
  supplier_count: number | null;
  po_count: number | null;
  open_qty: number | null;
}

export interface AgeingRow {
  bucket_seq: number | null;
  bucket_label: string | null;
  measure_value: number | null;
  po_count: number | null;
}

export interface SupplierSdpRow {
  t2_group: string | null;
  material_class: string | null;
  factory_count: number | null;
  measure_value: number | null;
  sdp_pct: number | null;
  sdp_on_time_qty: number | null;
  sdp_delivered_qty: number | null;
  pos_total: number | null;
  pos_late: number | null;
  avg_delay_days: number | null;
  /** Position counted from the worst end of the same ranking — see `supplier-sdp.sql`. */
  rank_worst: number | null;
}

/** How the active measure is named in a heading or beside a figure. */
export function measureLabel(measure: string | null | undefined): string {
  return measure === 'value' ? 'Value (HKD)' : 'Volume';
}

/** How the active order state is named mid-sentence: "value ordered", "volume on order". */
export function orderStateWord(orderState: string | null | undefined): string {
  return orderState === 'received' ? 'received' : orderState === 'open' ? 'on order' : 'ordered';
}

/**
 * Axis ticks, bar labels and KPI figures. Committed quantity runs to nine figures and
 * "400,000,000" is wider than the gutter a chart reserves for it — the full figure stays in the
 * tooltip and the table.
 */
export const compact = new Intl.NumberFormat(LOCALE, {
  notation: 'compact',
  maximumFractionDigits: 1,
});

/** A percentage as the page shows it. An absent figure is an em dash, never a zero. */
export function pct(value: number | null | undefined, digits = 1): string {
  return typeof value === 'number' && Number.isFinite(value) ? `${value.toFixed(digits)}%` : '—';
}

/** A measure figure, compacted. An absent figure is an em dash, never a zero. */
export function measureFigure(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) ? compact.format(value) : '—';
}

export function asNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/**
 * A bar's fill, as a percentage of its track. Clamped, because a runtime-computed width is the one
 * inline style §4.8 permits and an unclamped one escapes its track.
 */
export function widthPct(value: number | null | undefined, of = 100): string {
  if (typeof value !== 'number' || !Number.isFinite(value) || of <= 0) {
    return '0%';
  }
  return `${Math.max(0, Math.min(100, (value / of) * 100))}%`;
}

/**
 * Whether an SDP figure has met the one global target. Never colour alone — pair it with a label
 * (§4.6).
 *
 * Decided on the quantities when they are available, not on the published percentage. `sdp_pct`
 * arrives rounded to two decimals, so a supplier that delivered 224,389 of 224,395 units on time
 * publishes as 100.00 and would read "at target" on the same row that reports late orders. At a
 * 100% target "met" means every delivered unit was on time, which the integer pair answers exactly
 * and a rounded ratio cannot. The percentage stays the fallback for a figure that travels without
 * its quantities.
 */
export function meetsTarget(
  sdpPct: number | null | undefined,
  onTimeQty?: number | null,
  deliveredQty?: number | null,
): boolean | null {
  if (
    typeof onTimeQty === 'number' &&
    Number.isFinite(onTimeQty) &&
    typeof deliveredQty === 'number' &&
    Number.isFinite(deliveredQty) &&
    deliveredQty > 0
  ) {
    return onTimeQty >= deliveredQty;
  }
  return typeof sdpPct === 'number' && Number.isFinite(sdpPct) ? sdpPct >= SDP_TARGET_PCT : null;
}
